/**
 * RuleConditionsEditor (FE-4, PHASE1 §7.4). Edits the ordered list of
 * `{field, op, value}` conditions. The operator dropdown is driven by
 * `opsForField(field)` so text fields offer contains/equals/starts_with/
 * ends_with and `amount` offers the numeric comparators. Changing the field
 * resets the operator when the current one is no longer valid.
 */
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button.tsx';
import { Input } from '../../components/ui/Input.tsx';
import { Select, type SelectOption } from '../../components/ui/Select.tsx';
import { CONDITION_FIELD, CONDITION_OP, opsForField } from '../../lib/labels.ts';
import { blankCondition, type EditableCondition } from '../../lib/hooks/useRules.ts';
import type { RuleField, RuleOperator } from '../../lib/types.ts';

const FIELD_OPTIONS: SelectOption[] = (Object.keys(CONDITION_FIELD) as RuleField[]).map(
  (field) => ({ value: field, label: CONDITION_FIELD[field] }),
);

function opOptions(field: RuleField): SelectOption[] {
  return opsForField(field).map((op) => ({ value: op, label: CONDITION_OP[op] }));
}

export interface RuleConditionsEditorProps {
  conditions: EditableCondition[];
  onChange: (next: EditableCondition[]) => void;
}

export function RuleConditionsEditor({ conditions, onChange }: RuleConditionsEditorProps) {
  function update(key: string, patch: Partial<EditableCondition>) {
    onChange(conditions.map((c) => (c._key === key ? { ...c, ...patch } : c)));
  }

  function changeField(key: string, field: RuleField) {
    const current = conditions.find((c) => c._key === key);
    const validOps = opsForField(field);
    const op: RuleOperator = current && validOps.includes(current.op) ? current.op : validOps[0];
    update(key, { field, op });
  }

  function remove(key: string) {
    onChange(conditions.filter((c) => c._key !== key));
  }

  function add() {
    onChange([...conditions, blankCondition()]);
  }

  return (
    <div className="space-y-2">
      {conditions.length === 0 ? (
        <p className="text-sm text-slate-400 dark:text-slate-500">
          Noch keine Bedingung. Füge mindestens eine hinzu.
        </p>
      ) : null}

      {conditions.map((condition) => (
        <div key={condition._key} className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="sm:w-40">
            <Select
              value={condition.field}
              onValueChange={(next) => changeField(condition._key, next as RuleField)}
              options={FIELD_OPTIONS}
            />
          </div>
          <div className="sm:w-52">
            <Select
              value={condition.op}
              onValueChange={(next) => update(condition._key, { op: next as RuleOperator })}
              options={opOptions(condition.field)}
            />
          </div>
          <div className="flex-1">
            <Input
              value={condition.value}
              onChange={(e) => update(condition._key, { value: e.target.value })}
              placeholder={condition.field === 'amount' ? 'z. B. -49.90' : 'Wert'}
              inputMode={condition.field === 'amount' ? 'decimal' : 'text'}
              aria-label="Bedingungswert"
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => remove(condition._key)}
            aria-label="Bedingung entfernen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}

      <Button variant="secondary" size="sm" onClick={add}>
        <Plus className="h-4 w-4" />
        Bedingung
      </Button>
    </div>
  );
}
